/* Amares Homes — the buy column's derived figures.
 *
 * Two things beside the picker have to follow the selection: the overall-size
 * line and the total. Craft replaces the whole <variant-selects> element from
 * the server on every change, so the size cards look after themselves; these
 * two sit outside it and ride the same variantChange event the add-on uses.
 *
 * Money is never computed here. Liquid emits every total already formatted, so
 * a change of currency or of the shop's money format needs no code change.
 */
(function () {
  'use strict';

  function parse(el) {
    if (!el) return null;
    try {
      return JSON.parse(el.textContent);
    } catch (e) {
      return null;
    }
  }

  /* ---------------------------------------------------------------- specs */

  function Spec(root) {
    this.root = root;
    this.text = root.querySelector('[data-amx-spec-text]');
    this.rows = parse(root.querySelector('[data-amx-spec-rows]')) || {};
    this.sizeIndex = parseInt(root.dataset.amxSpecSizeIndex, 10);
    this.template = root.dataset.amxSpecTemplate || '';
    this.mattressLabel = root.dataset.amxSpecMattressLabel || '';
    if (!this.text || this.sizeIndex < 0) return;
    this.bound = true;
  }

  Spec.prototype.update = function (variant) {
    if (!this.bound || !variant) return;
    var options = variant.options || [variant.option1, variant.option2, variant.option3];
    var row = this.rows[options[this.sizeIndex]];

    // A size with no measurements yet loses the line rather than showing a
    // half-filled one with gaps where the figures should be.
    if (!row) {
      this.root.hidden = true;
      return;
    }
    this.root.hidden = false;

    var line = this.template
      .replace('[size]', options[this.sizeIndex])
      .replace('[w]', row.overall)
      .replace('[d]', row.length)
      .replace('[h]', row.headboard);

    this.text.textContent = line;
    if (row.mattress) {
      var sep = document.createElement('span');
      sep.className = 'amx-spec__sep';
      sep.setAttribute('aria-hidden', 'true');
      sep.textContent = '·';
      this.text.appendChild(sep);
      this.text.appendChild(
        document.createTextNode(this.mattressLabel + ' ' + row.mattress)
      );
    }
  };

  /* ------------------------------------------------------------ formatting */

  /* Shopify's money format is a Liquid string with one of these placeholders.
   * Reimplemented rather than assumed, because the shop's currency and format
   * are settings the merchant can change under us. */
  var GROUPINGS = {
    amount: [2, ',', '.'],
    amount_no_decimals: [0, ',', '.'],
    amount_with_comma_separator: [2, '.', ','],
    amount_no_decimals_with_comma_separator: [0, '.', ','],
    amount_with_apostrophe_separator: [2, "'", '.'],
    amount_with_space_separator: [2, ' ', ','],
    amount_no_decimals_with_space_separator: [0, ' ', ','],
  };

  function formatMoney(cents, format) {
    if (typeof cents !== 'number' || !format) return null;
    var out = null;

    format.replace(/\{\{\s*(\w+)\s*\}\}/, function (match, name) {
      var rule = GROUPINGS[name];
      if (!rule) return match;

      var places = rule[0];
      var thousands = rule[1];
      var decimal = rule[2];

      var value = (cents / 100).toFixed(places);
      var parts = value.split('.');
      var whole = parts[0].replace(/\B(?=(\d{3})+(?!\d))/g, thousands);

      out = format.replace(match, places ? whole + decimal + parts[1] : whole);
      return match;
    });

    return out;
  }

  /* ---------------------------------------------------------------- total */

  function Total(root) {
    this.root = root;
    this.amount = root.querySelector('[data-amx-total-amount]');
    this.note = root.querySelector('[data-amx-total-note]');
    this.map = parse(root.querySelector('[data-amx-total-map]')) || {};
    this.variantId = parseInt(root.dataset.amxTotalVariant, 10) || null;
    this.format = root.dataset.amxTotalFormat || '';
    this.keepQty = root.dataset.amxTotalKeepQty !== 'false';
    this.frameNote = this.note ? this.note.textContent.trim() : '';
    this.unitNote = root.dataset.amxTotalUnitNote || '';
    this.withFrame = false;
    /* What the visitor last asked for, which is not always what is in the box:
     * Craft resets the input to the variant's minimum on every variant change,
     * because quantity rules can differ per variant. */
    this.wanted = 1;
    if (!this.amount) return;
    this.bound = true;

    /* Prove the formatter against Liquid's own output before trusting it with
     * a price. If the two disagree — an unusual money format, a currency this
     * does not cover — quantity is left out of the total rather than shown
     * wrong. A total nobody can rely on is worse than one that does less. */
    var seed = this.map[this.variantId];
    this.canMultiply = !!(
      seed &&
      typeof seed.unit === 'number' &&
      formatMoney(seed.unit, this.format) === seed.alone
    );
  }

  /** The input, not a remembered copy of it. */
  Total.prototype.input = function () {
    var scope = this.root.closest('product-info') || document;
    return scope.querySelector('input[name="quantity"]');
  };

  Total.prototype.readQty = function () {
    var el = this.input();
    var n = el ? parseInt(el.value, 10) : 1;
    return n && n > 0 ? n : 1;
  };

  /** Put the visitor's quantity back after Craft has reset it, within whatever
   *  the new variant allows. Craft resets because the rules can change; it has
   *  no way of knowing they usually do not. */
  Total.prototype.restoreQty = function () {
    if (!this.keepQty || this.wanted <= 1) return;
    var el = this.input();
    if (!el) return;

    var want = this.wanted;
    var min = parseInt(el.min, 10);
    var max = parseInt(el.max, 10);
    if (min && want < min) want = min;
    if (max && want > max) want = max;
    if (String(want) === String(el.value)) return;

    el.value = want;
    // Craft's own +/- buttons announce themselves this way, so everything
    // else watching the box stays in step.
    el.dispatchEvent(new Event('change', { bubbles: true }));
  };

  Total.prototype.update = function (variant) {
    if (!this.bound) return;
    if (variant && variant.id) this.variantId = variant.id;

    var entry = this.map[this.variantId];
    if (!entry) return;

    var showFrame = this.withFrame && entry.with_frame;
    // Read at paint time. A cached copy went stale the moment Craft rewrote
    // the input, and the total then claimed a quantity the page did not show.
    var qty = this.canMultiply ? this.readQty() : 1;

    if (qty > 1) {
      var unit = showFrame ? entry.unit_with_frame : entry.unit;
      var multiplied = formatMoney(unit * qty, this.format);
      // One last guard: never paint a null over a real price.
      this.amount.textContent = multiplied || (showFrame ? entry.with_frame : entry.alone);
    } else {
      this.amount.textContent = showFrame ? entry.with_frame : entry.alone;
    }

    if (this.note) {
      var base = showFrame ? this.frameNote : this.unitNote;
      var text = qty > 1 ? qty + ' × ' + base : base;
      this.note.textContent = text;
      // At one, the note only earns its place when it says something the
      // price does not — that the frame is included.
      this.note.hidden = !(showFrame || qty > 1);
    }
  };

  Total.prototype.setFrame = function (on) {
    this.withFrame = !!on;
    this.update(null);
  };

  Total.prototype.setQty = function () {
    this.wanted = this.readQty();
    this.update(null);
  };

  /* ----------------------------------------------------------------- boot */

  // Module scope, not per-boot: a section can reload on its own, and the
  // subscriptions below are registered once for whatever exists at the time.
  var specs = [];
  var totals = [];
  var listening = false;

  function frameOn() {
    var check = document.querySelector('[data-amx-addon-check]');
    return !!(check && check.checked && !check.disabled);
  }

  function boot(scope) {
    var where = scope || document;

    Array.prototype.forEach.call(where.querySelectorAll('[data-amx-spec]'), function (el) {
      if (el.dataset.amxBound) return;
      el.dataset.amxBound = '1';
      specs.push(new Spec(el));
    });

    Array.prototype.forEach.call(where.querySelectorAll('[data-amx-total]'), function (el) {
      if (el.dataset.amxBound) return;
      el.dataset.amxBound = '1';
      var t = new Total(el);
      totals.push(t);
      // Opening state: the add-on may be pre-ticked, or absent in this size,
      // and the quantity box may already hold more than one.
      t.wanted = t.readQty();
      t.setFrame(frameOn());
    });

    if (listening) return;
    listening = true;

    if (typeof PUB_SUB_EVENTS !== 'undefined' && typeof subscribe === 'function') {
      subscribe(PUB_SUB_EVENTS.variantChange, function (event) {
        if (!event || !event.data) return;
        specs.forEach(function (s) {
          s.update(event.data.variant);
        });
        totals.forEach(function (t) {
          // Craft has already reset the quantity box by the time this fires —
          // setQuantityBoundries runs inside updateQuantityRules, well before
          // the event — so the visitor's quantity goes back first.
          t.restoreQty();
          // The add-on re-syncs on this same event and may have just
          // disabled itself. Where the frame does not exist in the new size
          // the map has no combined figure either, so the total falls back to
          // the bed alone whichever order the two subscribers ran in.
          t.withFrame = frameOn();
          t.update(event.data.variant);
        });
      });
    }

    // Delegated, because the add-on block can be re-rendered independently.
    document.addEventListener('change', function (e) {
      if (!e.target.matches || !e.target.matches('[data-amx-addon-check]')) return;
      totals.forEach(function (t) {
        t.setFrame(frameOn());
      });
    });

    /* Craft's quantity widget writes the value and fires `change`; typing in
     * the box fires `input`. Both are listened for so the total keeps up with
     * the stepper and the keyboard alike. */
    var onQty = function (e) {
      if (!e.target.matches || !e.target.matches('input[name="quantity"]')) return;
      totals.forEach(function (t) {
        t.setQty();
      });
    };
    document.addEventListener('change', onQty);
    document.addEventListener('input', onQty);

    /* Craft changes the box itself in a couple of places and says so only
     * through this event — no `change` is dispatched on the input. Without
     * listening the total would keep quoting a quantity nobody can see. */
    if (typeof PUB_SUB_EVENTS !== 'undefined' && typeof subscribe === 'function') {
      subscribe(PUB_SUB_EVENTS.quantityUpdate, function () {
        totals.forEach(function (t) {
          t.update(null);
        });
      });
    }
  }

  if (document.readyState !== 'loading') boot();
  else document.addEventListener('DOMContentLoaded', function () { boot(); });

  document.addEventListener('shopify:section:load', function (e) { boot(e.target); });
})();
